/**
 * Resolução da camada de mapeamento cross-tenant (SPEC-15 §7).
 *
 * Traduz uma entidade da ORIGEM (produto / one1_outros) para o equivalente no
 * tenant DESTINO, sem assumir id igual. Também resolve unidade_medida por chave
 * natural (sigla). Usado para PRÉ-SELECIONAR o item de destino no vínculo.
 *
 * Contexto: usa o override de operação (token/tenant do DESTINO) e envia o
 * sourceTenantId da ORIGEM (lido do contexto). Consome:
 *   GET /cross-tenant/mapping/resolve?sourceTenantId=&entityType=&sourceEntityId=
 *   GET /cross-tenant/umd/resolve?sourceTenantId=&sourceUmdId=
 */

import { api, type ApiOverrideOptions } from "../../../../lib/api";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import { QuotationOperationContextStore } from "./quotationOperationContext";

export type CrossTenantEntityType = "produto" | "one1_outros";

export interface ResolvedTargetEntity {
	tenantId: string;
	entityType: string;
	entityId: number;
	role: string | null;
}

export interface EntityMappingResult {
	groupId: number;
	relationshipType: string;
	targets: ResolvedTargetEntity[];
}

export interface UnidadeMedidaResult {
	umdId: number;
	sigla: string;
}

interface ApiSuccess<T> {
	status?: string;
	data?: T;
}

function requireContext(): { sourceTenantId: string; override: ApiOverrideOptions } {
	const context = QuotationOperationContextStore.get();
	if (!context?.sourceTenantId) {
		throw new Error(
			"Contexto cross-tenant indisponível — selecione o cliente roteado novamente.",
		);
	}
	return {
		sourceTenantId: context.sourceTenantId,
		override: {
			accessTokenOverride: context.operationAccessToken,
			tenantIdOverride: context.effectiveTenantId,
		},
	};
}

/**
 * Resolve o produto/entidade da origem no tenant destino.
 * Repassa o erro do backend (422 NOT_FOUND, 409 AMBIGUOUS, 400 UNSUPPORTED)
 * para o chamador decidir a UX.
 */
export async function resolveEntityMapping(params: {
	entityType: CrossTenantEntityType;
	sourceEntityId: number;
}): Promise<EntityMappingResult> {
	const { sourceTenantId, override } = requireContext();

	const query = new URLSearchParams({
		sourceTenantId,
		entityType: params.entityType,
		sourceEntityId: String(params.sourceEntityId),
	}).toString();

	sendToTerminal(
		"🧭 [CROSS_TENANT] Resolvendo mapeamento de entidade",
		{ sourceTenantId, entityType: params.entityType, sourceEntityId: params.sourceEntityId },
		LogCategory.BUSINESS,
	);

	const response = (await api(
		`/cross-tenant/mapping/resolve?${query}`,
		{ method: "GET" },
		override,
	)) as ApiSuccess<EntityMappingResult>;

	if (!response?.data) {
		throw new Error("Resposta invalida da resolucao de mapeamento.");
	}
	return response.data;
}

/**
 * Resolve a unidade de medida da origem no destino por sigla (chave natural).
 */
export async function resolveUnidadeMedida(params: {
	sourceUmdId: number;
}): Promise<UnidadeMedidaResult> {
	const { sourceTenantId, override } = requireContext();

	const query = new URLSearchParams({
		sourceTenantId,
		sourceUmdId: String(params.sourceUmdId),
	}).toString();

	const response = (await api(
		`/cross-tenant/umd/resolve?${query}`,
		{ method: "GET" },
		override,
	)) as ApiSuccess<UnidadeMedidaResult>;

	if (!response?.data) {
		throw new Error("Resposta invalida da resolucao de unidade de medida.");
	}
	return response.data;
}
